import { Card, CardContent } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Button } from "@/shared/ui/button";
import { Search, Loader2, X } from "lucide-react";
import { getStatusLabel } from "./clientAssetsHelpers";
import type { FilterOptions } from "./useFilterOptions";

interface Property {
  id: string;
  name: string;
}

interface ClientAssetsFiltersProps {
  searchTerm: string;
  debouncedSearch: string;
  statusFilter: string;
  typeFilter: string;
  propertyFilter: string;
  properties: Property[];
  filterOptions: FilterOptions;
  assetsLoading: boolean;
  hasActiveFilters: boolean;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onTypeChange: (value: string) => void;
  onPropertyChange: (value: string) => void;
  onClearAll: () => void;
}

/** Search input + property/category/status selects + clear-all footer. */
export function ClientAssetsFilters({
  searchTerm,
  debouncedSearch,
  statusFilter,
  typeFilter,
  propertyFilter,
  properties,
  filterOptions,
  assetsLoading,
  hasActiveFilters,
  onSearchChange,
  onStatusChange,
  onTypeChange,
  onPropertyChange,
  onClearAll,
}: ClientAssetsFiltersProps) {
  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
      <CardContent className="pt-6">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              {/* MVP2-282: Show spinner during debounce OR data loading */}
              {(searchTerm !== debouncedSearch ||
                (assetsLoading && debouncedSearch)) && (
                <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
              )}
              <Input
                placeholder="Search by asset name, manufacturer, model, serial number..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-10 pr-10 bg-background/50"
              />
            </div>
            <Select value={propertyFilter} onValueChange={onPropertyChange}>
              <SelectTrigger className="w-full sm:w-48 bg-background/50">
                <SelectValue placeholder="All Properties" />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                <SelectItem value="all">All Properties</SelectItem>
                {properties.map((property) => (
                  <SelectItem key={property.id} value={property.id}>
                    {property.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={onTypeChange}>
              <SelectTrigger className="w-full sm:w-48 bg-background/50">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                <SelectItem value="all">All Categories</SelectItem>
                {filterOptions.categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={onStatusChange}>
              <SelectTrigger className="w-full sm:w-48 bg-background/50">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                <SelectItem value="all">All Status</SelectItem>
                {filterOptions.statuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {getStatusLabel(status)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {hasActiveFilters && (
            <div className="flex items-center justify-between pt-2 border-t">
              <p className="text-sm text-muted-foreground">
                {
                  [
                    searchTerm && "search",
                    statusFilter !== "all" && "status",
                    typeFilter !== "all" && "category",
                    propertyFilter !== "all" && "property",
                  ].filter(Boolean).length
                }{" "}
                filter(s) active
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearAll}
                className="h-8 gap-2"
              >
                <X className="h-4 w-4" />
                Clear All Filters
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
